import React from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { MapPin } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { thumbUrl } from '../utils/imageUrl';

// Fix for default marker icons in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const PropertyMap = ({ properties, onPropertyClick, selectedPropertyId }) => {
  // Filter properties that have coordinates
  const propertiesWithCoords = properties.filter(
    (p) => p.coordinates && p.coordinates.lat && p.coordinates.lng
  );

  // Calculate map bounds to fit all properties
  const bounds = propertiesWithCoords.length > 0
    ? propertiesWithCoords.map(p => [p.coordinates.lat, p.coordinates.lng])
    : null;

  if (propertiesWithCoords.length === 0) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-100 rounded-lg">
        <div className="text-center">
          <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-600">No properties with locations available</p>
        </div>
      </div>
    );
  }

  // Calculate center point (Montreal downtown)
  const center = [45.5017, -73.5673]; // Montreal city center

  // Custom marker icon for selected property
  const createCustomIcon = (isSelected) => {
    return L.divIcon({
      className: 'custom-marker',
      html: `
        <div style="
          background-color: ${isSelected ? '#e11d48' : '#f43f5e'};
          width: 32px;
          height: 32px;
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <div style="
            transform: rotate(45deg);
            color: white;
            font-weight: bold;
            font-size: 14px;
          ">📍</div>
        </div>
      `,
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      popupAnchor: [0, -32],
    });
  };

  return (
    <div className="h-full w-full rounded-lg overflow-hidden shadow-lg">
      <MapContainer
        center={center}
        zoom={propertiesWithCoords.length === 1 ? 14 : 13}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
        maxZoom={18}
        minZoom={11}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {propertiesWithCoords.map((property) => {
          const propertyId = property._id || property.id;
          const isSelected = propertyId === selectedPropertyId;
          
          return (
            <Marker
              key={propertyId}
              position={[property.coordinates.lat, property.coordinates.lng]}
              icon={createCustomIcon(isSelected)}
              eventHandlers={{
                click: () => {
                  if (onPropertyClick) {
                    onPropertyClick(property);
                  }
                },
              }}
            >
              <Popup>
                <div className="p-2 min-w-[200px]">
                  <img
                    src={thumbUrl(property.images[0])}
                    alt={property.title}
                    className="w-full h-32 object-cover rounded-lg mb-2"
                  />
                  <h3 className="font-semibold text-sm mb-1">{property.title}</h3>
                  <p className="text-xs text-gray-600 mb-1">
                    {property.neighborhood || property.location}
                  </p>
                  <p className="text-sm font-bold text-rose-600">
                    CA${property.price}/night
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    ⭐ {property.rating} ({property.reviews_count} reviews)
                  </p>
                  <button
                    onClick={() => onPropertyClick && onPropertyClick(property)}
                    className="mt-2 w-full bg-rose-500 text-white text-xs py-1 px-2 rounded hover:bg-rose-600"
                  >
                    View Details
                  </button>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
};

export default PropertyMap;
